import { FC, UIEvent } from 'react'
import en from 'src/constants/en'
import {
  KeywordFetchSentimentAnalysis,
  AnalyticsFetchSentimentAnalysisResponseData,
  AnalyticsFetchThreadsResponseDataItem,
  AnalyticsFetchThreadsSentimentType,
  AnalyticsFetchThreadsIsLostType,
} from 'src/services/__generated__/api'
import { ReactComponent as StatusDot } from 'src/assets/svg/status-dot-red.svg'
import styles from './sentimentAnalysesSection.module.scss'
import { SentimentAnalysesNotifications } from './SentimentAnalysesNotifications'
import { FilterPanel } from './FilterPanel'
import { isAnalyticsFetchSentimentAnalysisResponseData } from './typeGuards/isAnalyticsFetchSentimentAnalysisResponseData'

interface SentimentAnalysesSectionProps {
  allThreadsData?: AnalyticsFetchThreadsResponseDataItem[]
  applyFilters?: (params: {
    isLost: AnalyticsFetchThreadsIsLostType
    searchText: string
    selectedKeywords: string[]
    selectedLanguages: string[]
    selectedMentionFormats: string[]
    selectedMentionTypes: string[]
    selectedSources: string[]
  }) => void
  chartData?:
    | AnalyticsFetchSentimentAnalysisResponseData
    | KeywordFetchSentimentAnalysis
  handleThreadsScroll?: (
    event: UIEvent,
    sentiment?: AnalyticsFetchThreadsSentimentType
  ) => void
  hasFilters?: boolean
  isLoading?: boolean
  negativeThreadsData?: AnalyticsFetchThreadsResponseDataItem[]
  neutralThreadsData?: AnalyticsFetchThreadsResponseDataItem[]
  onSearchTextChange?: (text: string) => void
  onViewThreadDetails?: (threadId: string) => void
  positiveThreadsData?: AnalyticsFetchThreadsResponseDataItem[]
}

export const SentimentAnalysesSection: FC<SentimentAnalysesSectionProps> = ({
  chartData,
  allThreadsData,
  negativeThreadsData,
  neutralThreadsData,
  positiveThreadsData,
  handleThreadsScroll,
  applyFilters,
  onSearchTextChange,
  isLoading,
  onViewThreadDetails,
}) => {
  const handleSearchTextChange = (text: string) => {
    onSearchTextChange?.(text)
  }

  const analyticsData = isAnalyticsFetchSentimentAnalysisResponseData(chartData)
    ? chartData
    : undefined

  const allKeywords = analyticsData?.allKeywords
  const allSources = analyticsData?.allSources
  const allLanguages = analyticsData?.allLanguages
  const allMentionFormats = analyticsData?.allMentionsFormat
  const allMentionTypes = analyticsData?.allMentionsType
  const selectedKeywords = analyticsData?.selectedKeywords
  const selectedSources = analyticsData?.selectedSources
  const selectedLanguages = analyticsData?.checkedLanguages
  const selectedMentionFormats = analyticsData?.selectedMentionFormat
  const selectedMentionTypes = analyticsData?.selectedMentionType
  const isLost = analyticsData?.isLost

  // No mentions in any sentiment bucket = nothing to show.
  const hasData = Boolean(
    allThreadsData?.length ||
      positiveThreadsData?.length ||
      negativeThreadsData?.length ||
      neutralThreadsData?.length
  )

  // A campaign that genuinely has no mentions gets the centered "not enough
  // data" empty state (instead of an empty donut + blank list + filters). But
  // once a campaign has content, an empty result means the current search/filter
  // matched nothing — keep the widget (chart + filters + mentions list) mounted
  // so the user can adjust the filter, and let the mentions column show its own
  // "no data" message. Otherwise the whole widget disappeared on an empty search
  // and the user was stuck until reload.
  const campaignHasContent = Boolean(
    analyticsData?.allKeywords?.length || analyticsData?.allSources?.length
  )

  return (
    <div className={styles.sentimentAnalysesSection}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <StatusDot className={styles.statusDot} />
          <div className={styles.title}>{en.sentimentAnalyses}</div>
        </div>
        <a className={styles.exploreLink} href="#">
          Explore
        </a>
      </div>

      {!hasData && !isLoading && !campaignHasContent ? (
        <div className={styles.emptyState}>{en.notEnoughDataToDisplay}</div>
      ) : (
        <div className={styles.chartWrapper}>
          <div className={styles.leftSection}>
            <SentimentAnalysesNotifications
              allThreadsData={allThreadsData}
              handleThreadsScroll={handleThreadsScroll}
              hasFilters={false}
              isSearching={isLoading}
              negativeThreadsData={negativeThreadsData}
              neutralThreadsData={neutralThreadsData}
              onViewThreadDetails={onViewThreadDetails}
              positiveThreadsData={positiveThreadsData}
            />
          </div>

          <div className={styles.rightSection}>
            <FilterPanel
              allKeywords={allKeywords}
              allLanguages={allLanguages}
              allMentionFormats={allMentionFormats}
              allMentionTypes={allMentionTypes}
              allSources={allSources}
              chartData={chartData}
              isLost={isLost}
              onApplyFilters={(params) => applyFilters?.(params)}
              onSearchTextChange={handleSearchTextChange}
              selectedKeywords={selectedKeywords}
              selectedLanguages={selectedLanguages}
              selectedMentionFormats={selectedMentionFormats}
              selectedMentionTypes={selectedMentionTypes}
              selectedSources={selectedSources}
            />
          </div>
        </div>
      )}
    </div>
  )
}
